import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { User } from './lib/supabase-auth';
import { isAdmin } from './lib/admin';
import AppShell from './components/AppShell';
import AuthModal from './components/AuthModal';
import FirstNamePrompt from './components/FirstNamePrompt';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [hasLifetimeAccess, setHasLifetimeAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showFirstNamePrompt, setShowFirstNamePrompt] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const refreshUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      console.log('Refreshing profile for user:', user.id);
      
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('has_lifetime_access, is_admin, purchase_amount, purchase_currency, purchased_at')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching profile:', error);
        return false;
      }
      
      if (profile) {
        console.log('Updated profile status:', {
          hasLifetimeAccess: profile.has_lifetime_access,
          isAdmin: profile.is_admin,
          purchaseAmount: profile.purchase_amount,
          purchaseCurrency: profile.purchase_currency,
          purchasedAt: profile.purchased_at
        });
        setHasLifetimeAccess(profile.has_lifetime_access);
        setIsAdminUser(profile.is_admin);
        return profile.has_lifetime_access;
      }
      return false;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      return false;
    }
  };

  useEffect(() => {
    // Check for payment success/canceled status
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      let pollingInterval: NodeJS.Timeout;
      let attempts = 0;
      const MAX_ATTEMPTS = 10;
      const POLL_INTERVAL = 2000; // 2 seconds

      const pollForPaymentConfirmation = async () => {
        const hasAccess = await refreshUserProfile();
        attempts++;

        if (hasAccess) {
          clearInterval(pollingInterval);
          // Navigate to profile page first, then show success message
          navigate('/profile', { replace: true });
          toast.success('Payment successful! Your account has been upgraded to lifetime access.', {
            duration: 5000,
          });
        } else if (attempts >= MAX_ATTEMPTS) {
          clearInterval(pollingInterval);
          navigate('/profile', { replace: true });
          toast.error('Payment confirmation taking longer than expected. Please contact support if issues persist.', {
            duration: 7000,
          });
        }
      };

      // Start polling immediately
      pollForPaymentConfirmation();
      pollingInterval = setInterval(pollForPaymentConfirmation, POLL_INTERVAL);

      return () => {
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }
      };
    } else if (canceled === 'true') {
      toast.error('Payment canceled. Please try again if you want to upgrade.', {
        duration: 5000,
      });
      // Remove query parameters
      navigate('/profile', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        if (session.user.email) {
          Promise.all([
            isAdmin(session.user.email),
            supabase
              .from('profiles')
              .select('has_lifetime_access')
              .eq('id', session.user.id)
              .single()
          ]).then(([adminStatus, { data: profile }]) => {
            setIsAdminUser(adminStatus);
            setHasLifetimeAccess(profile?.has_lifetime_access || false);
            setIsLoading(false);
          });
        } else {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser(session.user);
        if (session.user.email) {
          Promise.all([
            isAdmin(session.user.email),
            supabase
              .from('profiles')
              .select('has_lifetime_access')
              .eq('id', session.user.id)
              .single()
          ]).then(([adminStatus, { data: profile }]) => {
            setIsAdminUser(adminStatus);
            setHasLifetimeAccess(profile?.has_lifetime_access || false);
          });
        }
      } else {
        setUser(null);
        setIsAdminUser(false);
        setHasLifetimeAccess(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setShowFirstNamePrompt(true);
  };

  const handleFirstNameSubmit = async (firstName: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('profiles')
        .update({ first_name: firstName })
        .eq('id', user.id);

      setShowFirstNamePrompt(false);
      
      // Check for stored takeaway
      const storedTakeaway = localStorage.getItem('stored_takeaway');
      if (storedTakeaway) {
        navigate('/dashboard');
      } else {
        navigate('/chat');
      }
    } catch (error) {
      console.error('Error updating first name:', error);
    }
  };

  const handleUpgradeClick = async () => {
    if (isUpgrading) return;
    setIsUpgrading(true);

    try {
      console.log('[Stripe] Starting checkout process...');

      const { data: { session } } = await supabase.auth.getSession();
      console.log('[Stripe] Session check:', session ? 'Session found' : 'No session');

      if (!session?.access_token) {
        console.error('[Stripe] No access token available');
        toast.error('Please sign in to upgrade your account');
        throw new Error('No access token available');
      }

      console.log('[Stripe] Fetching checkout session from:', `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
      });

      console.log('[Stripe] Checkout response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[Stripe] Checkout creation failed:', errorData);
        toast.error(`Failed to create checkout session: ${errorData.error || response.statusText}`);
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('[Stripe] Checkout data received:', data);

      if (!data.sessionId) {
        console.error('[Stripe] No session ID in response:', data);
        toast.error('Invalid response from payment server');
        throw new Error('No session ID returned from checkout creation');
      }

      console.log('[Stripe] Loading Stripe.js with key:', import.meta.env.VITE_STRIPE_PUBLIC_KEY?.substring(0, 20) + '...');
      const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

      if (!stripe) {
        console.error('[Stripe] Failed to load Stripe.js');
        toast.error('Failed to load payment system. Please check your internet connection.');
        throw new Error('Failed to load Stripe');
      }

      console.log('[Stripe] Redirecting to checkout with session ID:', data.sessionId);
      const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });

      if (error) {
        console.error('[Stripe] Redirect error:', error);
        toast.error(`Payment redirect failed: ${error.message}`);
        throw error;
      }
    } catch (error) {
      console.error('[Stripe] Complete error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (!errorMessage.includes('access token')) {
        toast.error('Failed to start checkout process. Please try again.');
      }
    } finally {
      setIsUpgrading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50/50 to-pink-50/50">
        <div className="animate-pulse text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <AppShell
      isAuthenticated={!!user}
      isAdmin={isAdminUser}
      hasLifetimeAccess={hasLifetimeAccess}
      onUpgradeClick={handleUpgradeClick}
      isUpgrading={isUpgrading}
    >
      <Outlet />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        mode="sign-in"
      />

      <FirstNamePrompt
        isOpen={showFirstNamePrompt}
        onClose={() => setShowFirstNamePrompt(false)}
        onSubmit={handleFirstNameSubmit}
      />
    </AppShell>
  );
}

export default App;